import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../services/authService'
import toast from 'react-hot-toast'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts'

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'trafego', label: 'Trafego' },
  { key: 'seo', label: 'SEO' },
  { key: 'anuncios', label: 'Anuncios' },
  { key: 'email', label: 'Email' },
  { key: 'relatorio', label: 'Relatorio IA' }
]

const PERIOD_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 15, label: '15d' },
  { value: 30, label: '30d' },
  { value: 60, label: '60d' },
  { value: 90, label: '90d' }
]

const CHART_COLORS = ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#1e40af']

const formatNumber = (num) => {
  if (num == null) return '0'
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return Number(num).toLocaleString('pt-BR')
}

const formatCurrency = (value) => {
  if (value == null) return 'R$ 0,00'
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const formatPercent = (value) => {
  if (value == null) return '0%'
  return Number(value).toFixed(1) + '%'
}

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------
const KpiCard = ({ icon, value, label, format = 'number' }) => {
  let displayValue = value
  if (format === 'currency') displayValue = formatCurrency(value)
  else if (format === 'percent') displayValue = formatPercent(value)
  else displayValue = formatNumber(value)

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col items-center justify-center shadow-sm hover:shadow-md transition-shadow">
      <span className="text-2xl mb-2">{icon}</span>
      <span className="text-2xl font-bold text-gray-800">{displayValue}</span>
      <span className="text-xs text-gray-500 mt-1 text-center">{label}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Period Selector
// ---------------------------------------------------------------------------
const PeriodSelector = ({ days, onChange }) => (
  <div className="flex gap-1">
    {PERIOD_OPTIONS.map((opt) => (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
          days === opt.value
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
)

// ---------------------------------------------------------------------------
// Loading Spinner
// ---------------------------------------------------------------------------
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-16">
    <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600"></div>
  </div>
)

// ---------------------------------------------------------------------------
// Empty State
// ---------------------------------------------------------------------------
const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
    <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-2.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
    </svg>
    <p className="text-sm">{message || 'Nenhum dado disponivel para o periodo selecionado.'}</p>
  </div>
)

// ---------------------------------------------------------------------------
// Mini Table
// ---------------------------------------------------------------------------
const MiniTable = ({ title, columns, rows }) => (
  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
    </div>
    {rows && rows.length > 0 ? (
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            {columns.map((col) => (
              <th key={col.key} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
              {columns.map((col) => (
                <td key={col.key} className="px-4 py-2 text-gray-700">
                  {col.format ? col.format(row[col.key]) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <div className="px-4 py-6 text-center text-sm text-gray-400">Sem dados</div>
    )}
  </div>
)

// ===========================================================================
// TAB: Overview
// ===========================================================================
const OverviewTab = ({ days, setDays }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/api/marketing/analytics/overview?days=${days}`)
      setData(res.data?.data || res.data || {})
    } catch (err) {
      console.error('Erro ao carregar overview:', err)
      toast.error('Erro ao carregar dados de overview')
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => { fetchData() }, [fetchData])

  if (loading) return <LoadingSpinner />
  if (!data) return <EmptyState />

  const kpis = [
    { icon: '\uD83D\uDC65', value: data.visitors || data.visitantes || 0, label: 'Visitantes', format: 'number' },
    { icon: '\uD83D\uDCCA', value: data.sessions || data.sessoes || 0, label: 'Sessoes', format: 'number' },
    { icon: '\uD83C\uDFAF', value: data.new_leads || data.leads_novos || 0, label: 'Leads Novos', format: 'number' },
    { icon: '\u2705', value: data.converted_leads || data.leads_convertidos || 0, label: 'Leads Convertidos', format: 'number' },
    { icon: '\uD83D\uDCB0', value: data.ad_spend || data.gasto_ads || 0, label: 'Gasto Ads R$', format: 'currency' },
    { icon: '\uD83D\uDCC8', value: data.ad_roi || data.roi_ads || 0, label: 'ROI Ads %', format: 'percent' }
  ]

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <PeriodSelector days={days} onChange={setDays} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((kpi, i) => (
          <KpiCard key={i} {...kpi} />
        ))}
      </div>
    </div>
  )
}

// ===========================================================================
// TAB: Trafego
// ===========================================================================
const TrafegoTab = ({ days, setDays }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/api/marketing/analytics/traffic?days=${days}`)
      setData(res.data?.data || res.data || {})
    } catch (err) {
      console.error('Erro ao carregar trafego:', err)
      toast.error('Erro ao carregar dados de trafego')
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => { fetchData() }, [fetchData])

  if (loading) return <LoadingSpinner />
  if (!data) return <EmptyState />

  const chartData = data.daily || data.chart || []
  const topSources = data.top_sources || data.topSources || []
  const topPages = data.top_pages || data.topPages || []

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <PeriodSelector days={days} onChange={setDays} />
      </div>

      {/* Line Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Sessoes / Usuarios / Pageviews por Dia</h3>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="sessions" name="Sessoes" stroke="#2563eb" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="users" name="Usuarios" stroke="#10b981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="pageviews" name="Pageviews" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState message="Sem dados de trafego para o periodo." />
        )}
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <MiniTable
          title="Top Fontes de Trafego"
          columns={[
            { key: 'source', label: 'Fonte' },
            { key: 'sessions', label: 'Sessoes', format: formatNumber },
            { key: 'users', label: 'Usuarios', format: formatNumber }
          ]}
          rows={topSources.slice(0, 10)}
        />
        <MiniTable
          title="Top Paginas"
          columns={[
            { key: 'page', label: 'Pagina' },
            { key: 'pageviews', label: 'Pageviews', format: formatNumber },
            { key: 'avg_time', label: 'Tempo Medio' }
          ]}
          rows={topPages.slice(0, 10)}
        />
      </div>
    </div>
  )
}

// ===========================================================================
// TAB: SEO
// ===========================================================================
const SeoTab = ({ days, setDays }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/api/marketing/analytics/seo?days=${days}`)
      setData(res.data?.data || res.data || {})
    } catch (err) {
      console.error('Erro ao carregar SEO:', err)
      toast.error('Erro ao carregar dados de SEO')
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => { fetchData() }, [fetchData])

  if (loading) return <LoadingSpinner />
  if (!data) return <EmptyState />

  const chartData = data.daily || data.chart || []
  const keywords = data.keywords || []

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <PeriodSelector days={days} onChange={setDays} />
      </div>

      {/* Line Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Impressoes e Cliques por Dia</h3>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="impressions" name="Impressoes" stroke="#2563eb" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="clicks" name="Cliques" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState message="Sem dados de SEO para o periodo." />
        )}
      </div>

      {/* Keywords Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700">Rankings de Palavras-chave</h4>
        </div>
        {keywords.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Palavra-chave</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Impressoes</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cliques</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">CTR</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Posicao Media</th>
                </tr>
              </thead>
              <tbody>
                {keywords.slice(0, 20).map((kw, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-700 font-medium">{kw.query || kw.keyword}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{formatNumber(kw.impressions)}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{formatNumber(kw.clicks)}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{formatPercent(kw.ctr)}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{Number(kw.position || kw.avg_position || 0).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-4 py-6 text-center text-sm text-gray-400">Sem dados de keywords</div>
        )}
      </div>
    </div>
  )
}

// ===========================================================================
// TAB: Anuncios
// ===========================================================================
const AnunciosTab = ({ days, setDays }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/api/marketing/analytics/ads?days=${days}`)
      setData(res.data?.data || res.data || {})
    } catch (err) {
      console.error('Erro ao carregar anuncios:', err)
      toast.error('Erro ao carregar dados de anuncios')
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => { fetchData() }, [fetchData])

  if (loading) return <LoadingSpinner />
  if (!data) return <EmptyState />

  const chartData = data.daily || data.chart || []
  const kpis = [
    { icon: '\uD83D\uDC41\uFE0F', value: data.impressions || 0, label: 'Impressoes', format: 'number' },
    { icon: '\uD83D\uDD17', value: data.clicks || 0, label: 'Cliques', format: 'number' },
    { icon: '\uD83D\uDCB5', value: data.cost || data.spend || 0, label: 'Custo Total', format: 'currency' },
    { icon: '\uD83C\uDFAF', value: data.conversions || 0, label: 'Conversoes', format: 'number' },
    { icon: '\uD83D\uDCB2', value: data.cpc || 0, label: 'CPC Medio', format: 'currency' },
    { icon: '\uD83D\uDCCA', value: data.ctr || 0, label: 'CTR', format: 'percent' }
  ]

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <PeriodSelector days={days} onChange={setDays} />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((kpi, i) => (
          <KpiCard key={i} {...kpi} />
        ))}
      </div>

      {/* Bar Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Impressoes e Cliques por Dia</h3>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="impressions" name="Impressoes" fill="#2563eb" radius={[4, 4, 0, 0]} />
              <Bar dataKey="clicks" name="Cliques" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState message="Sem dados de anuncios para o periodo." />
        )}
      </div>
    </div>
  )
}

// ===========================================================================
// TAB: Email
// ===========================================================================
const EmailTab = ({ days, setDays }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/api/marketing/analytics/email?days=${days}`)
      setData(res.data?.data || res.data || {})
    } catch (err) {
      console.error('Erro ao carregar email:', err)
      toast.error('Erro ao carregar dados de email')
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => { fetchData() }, [fetchData])

  if (loading) return <LoadingSpinner />
  if (!data) return <EmptyState />

  const sent = Number(data.sent || 0)
  const delivered = Number(data.delivered || 0)
  const opened = Number(data.opened || 0)
  const clicked = Number(data.clicked || 0)
  const chartData = data.daily || data.chart || []

  const funnelSteps = [
    { label: 'Enviados', value: sent, color: 'bg-blue-500' },
    { label: 'Entregues', value: delivered, color: 'bg-blue-400' },
    { label: 'Abertos', value: opened, color: 'bg-green-500' },
    { label: 'Clicados', value: clicked, color: 'bg-emerald-500' }
  ]
  const maxFunnel = Math.max(sent, 1)

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <PeriodSelector days={days} onChange={setDays} />
      </div>

      {/* Funnel */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-6">Funil de Email Marketing</h3>
        <div className="space-y-4">
          {funnelSteps.map((step, i) => {
            const widthPct = Math.max((step.value / maxFunnel) * 100, 4)
            const rate = i === 0 ? 100 : ((step.value / (funnelSteps[i - 1].value || 1)) * 100)
            return (
              <div key={step.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{step.label}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-gray-800">{formatNumber(step.value)}</span>
                    {i > 0 && (
                      <span className="text-xs text-gray-400">({rate.toFixed(1)}%)</span>
                    )}
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-6">
                  <div
                    className={`${step.color} h-6 rounded-full transition-all duration-500`}
                    style={{ width: `${widthPct}%` }}
                  ></div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Line Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Enviados e Abertos por Dia</h3>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="sent" name="Enviados" stroke="#2563eb" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="opened" name="Abertos" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState message="Sem dados diarios de email para o periodo." />
        )}
      </div>
    </div>
  )
}

// ===========================================================================
// TAB: Relatorio IA
// ===========================================================================
const RelatorioIATab = () => {
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [collecting, setCollecting] = useState(false)

  const fetchReport = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/marketing/analytics/ai-report')
      setReport(res.data?.data || res.data || null)
    } catch (err) {
      console.error('Erro ao carregar relatorio IA:', err)
      if (err.response?.status !== 404) {
        toast.error('Erro ao carregar relatorio IA')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchReport() }, [fetchReport])

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      await api.post('/api/marketing/analytics/ai-report/generate')
      toast.success('Relatorio IA gerado com sucesso')
      await fetchReport()
    } catch (err) {
      console.error('Erro ao gerar relatorio:', err)
      toast.error('Erro ao gerar relatorio IA')
    } finally {
      setGenerating(false)
    }
  }

  const handleCollect = async () => {
    setCollecting(true)
    try {
      await api.post('/api/marketing/analytics/collect-now')
      toast.success('Metricas coletadas com sucesso')
      await fetchReport()
    } catch (err) {
      console.error('Erro ao coletar metricas:', err)
      toast.error('Erro ao coletar metricas')
    } finally {
      setCollecting(false)
    }
  }

  const getScoreColor = (score) => {
    const s = Number(score || 0)
    if (s < 30) return { text: 'text-red-600', bg: 'bg-red-100', border: 'border-red-300', ring: 'ring-red-500' }
    if (s < 70) return { text: 'text-yellow-600', bg: 'bg-yellow-100', border: 'border-yellow-300', ring: 'ring-yellow-500' }
    return { text: 'text-green-600', bg: 'bg-green-100', border: 'border-green-300', ring: 'ring-green-500' }
  }

  const getPrioridadeBadge = (prioridade) => {
    const p = (prioridade || '').toLowerCase()
    if (p === 'alta' || p === 'high') return 'bg-red-100 text-red-700 border-red-200'
    if (p === 'media' || p === 'medium') return 'bg-yellow-100 text-yellow-700 border-yellow-200'
    return 'bg-blue-100 text-blue-700 border-blue-200'
  }

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 justify-end">
        <button
          onClick={handleCollect}
          disabled={collecting}
          className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {collecting ? 'Coletando...' : 'Coletar Metricas Agora'}
        </button>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {generating ? 'Gerando...' : 'Gerar Novo Relatorio'}
        </button>
      </div>

      {!report ? (
        <EmptyState message="Nenhum relatorio IA disponivel. Clique em 'Gerar Novo Relatorio' para criar um." />
      ) : (
        <>
          {/* Health Score */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col items-center">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Score de Saude do Marketing</h3>
            <div className={`w-32 h-32 rounded-full flex items-center justify-center border-4 ${getScoreColor(report.score).border} ${getScoreColor(report.score).bg}`}>
              <span className={`text-4xl font-bold ${getScoreColor(report.score).text}`}>
                {Number(report.score || 0).toFixed(0)}
              </span>
            </div>
            <span className={`mt-2 text-sm font-medium ${getScoreColor(report.score).text}`}>
              {Number(report.score || 0) < 30 ? 'Critico' : Number(report.score || 0) < 70 ? 'Moderado' : 'Saudavel'}
            </span>
          </div>

          {/* Diagnostico */}
          {report.diagnostico && (
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Diagnostico</h3>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{report.diagnostico}</p>
            </div>
          )}

          {/* Recomendacoes */}
          {report.recomendacoes && report.recomendacoes.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Recomendacoes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {report.recomendacoes.map((rec, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${getPrioridadeBadge(rec.prioridade || rec.priority)}`}>
                        {rec.prioridade || rec.priority || 'Normal'}
                      </span>
                    </div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-1">{rec.titulo || rec.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{rec.descricao || rec.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Proximos Passos */}
          {report.proximos_passos && report.proximos_passos.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Proximos Passos</h3>
              <ul className="space-y-2">
                {report.proximos_passos.map((passo, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold mt-0.5">
                      {i + 1}
                    </span>
                    <span>{typeof passo === 'string' ? passo : passo.descricao || passo.description || ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Generated At */}
          {report.generated_at && (
            <p className="text-xs text-gray-400 text-right">
              Gerado em: {new Date(report.generated_at).toLocaleString('pt-BR')}
            </p>
          )}
        </>
      )}
    </div>
  )
}

// ===========================================================================
// MAIN PAGE COMPONENT
// ===========================================================================
const MarketingAnalyticsPage = () => {
  const [activeTab, setActiveTab] = useState('overview')
  const [days, setDays] = useState(30)

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab days={days} setDays={setDays} />
      case 'trafego':
        return <TrafegoTab days={days} setDays={setDays} />
      case 'seo':
        return <SeoTab days={days} setDays={setDays} />
      case 'anuncios':
        return <AnunciosTab days={days} setDays={setDays} />
      case 'email':
        return <EmailTab days={days} setDays={setDays} />
      case 'relatorio':
        return <RelatorioIATab />
      default:
        return <OverviewTab days={days} setDays={setDays} />
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Analytics Unificado</h1>
        <p className="text-sm text-gray-500 mt-1">Performance de marketing cross-channel</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1 -mb-px overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>{renderTab()}</div>
    </div>
  )
}

export default MarketingAnalyticsPage
